// frontend/src/pages/TrainingCoursePage.jsx
//
// Curso + visor de lección en una sola pantalla: índice a la izquierda,
// contenido a la derecha. En móvil el índice se pliega para que la lección
// ocupe todo el ancho.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Divider, IconButton,
  LinearProgress, List, ListItemButton, ListItemIcon, ListItemText, Paper,
  Stack, Typography, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ArrowBack as BackIcon,
  CheckCircle as DoneIcon,
  ExpandLess, ExpandMore,
  Menu as MenuIcon,
  NavigateBefore, NavigateNext,
  Lock as LockIcon,
  RadioButtonUnchecked as PendingIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

import { api } from '../services/api';
import ContentBlock from '../components/training/ContentBlock';

const TrainingCoursePage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [curso, setCurso] = useState(null);
  const [leccion, setLeccion] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [cargandoLeccion, setCargandoLeccion] = useState(false);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [abiertas, setAbiertas] = useState({});
  const [indiceVisible, setIndiceVisible] = useState(!isMobile);
  const [envios, setEnvios] = useState({});

  // Lista plana en orden, para "anterior / siguiente".
  const plano = useMemo(() => {
    if (!curso) return [];
    return curso.sections.flatMap((s) => s.lessons.map((l) => ({ ...l, section_id: s.id })));
  }, [curso]);

  const cargarCurso = useCallback((abrirId) => {
    setCargando(true);
    api.training.course(courseId)
      .then((res) => {
        setCurso(res.data);
        setAbiertas(Object.fromEntries((res.data.sections || []).map((s) => [s.id, true])));
        const destino = abrirId
          || res.data.progress?.current_lesson_id
          || res.data.sections?.[0]?.lessons?.[0]?.id;
        if (destino) abrirLeccion(destino);
      })
      .catch((err) => setError(err?.response?.status === 404
        ? 'Este curso no está disponible para tu cuenta.'
        : 'No se pudo cargar el curso.'))
      .finally(() => setCargando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  useEffect(() => {
    // Registrar el inicio no debe bloquear la carga: si falla, el curso se ve igual.
    api.training.start(courseId).catch(() => {});
    cargarCurso();
  }, [courseId, cargarCurso]);

  const abrirLeccion = (id) => {
    setCargandoLeccion(true);
    setAviso(null);
    api.training.lesson(id)
      .then((res) => {
        setLeccion(res.data);
        // Intentos previos de cada actividad, para mostrar "aprobada" o los
        // intentos restantes sin que el usuario tenga que reenviar.
        const actividades = (res.data.blocks || []).filter(
          (b) => ['quiz', 'question', 'task', 'confirmation'].includes(b.block_type));
        Promise.all(actividades.map((b) =>
          api.training.submissions(b.id)
            .then((r) => [b.id, r.data?.items || []])
            .catch(() => [b.id, []])
        )).then((pares) => setEnvios(Object.fromEntries(pares)));
      })
      // 423 = módulo bloqueado por un prerrequisito; el backend explica cuál.
      .catch((err) => setAviso({
        tipo: err?.response?.status === 423 ? 'warning' : 'error',
        texto: err?.response?.data?.detail || 'No se pudo abrir la lección.',
      }))
      .finally(() => setCargandoLeccion(false));
    if (isMobile) setIndiceVisible(false);
  };

  const completar = () => {
    api.training.completeLesson(leccion.id)
      .then((res) => {
        setAviso({ tipo: 'success', texto: `Lección completada · ${res.data.percent}% del curso` });
        cargarCurso(leccion.id);
      })
      // El backend rechaza con 409 y un motivo claro cuando la regla de
      // finalización no se cumple (checklist a medias, curso secuencial…).
      .catch((err) => setAviso({
        tipo: 'warning',
        texto: err?.response?.data?.detail || 'No se pudo completar la lección.',
      }));
  };

  const alternarItem = (blockId, itemKey, checked) => {
    api.training.toggleChecklist(blockId, itemKey, checked)
      .then(() => setLeccion((prev) => ({
        ...prev,
        checked_items: checked
          ? [...(prev.checked_items || []), itemKey]
          : (prev.checked_items || []).filter((k) => k !== itemKey),
      })))
      .catch(() => setAviso({ tipo: 'error', texto: 'No se pudo guardar el ítem.' }));
  };

  const enviarActividad = (blockId, payload) =>
    api.training.submitBlock(blockId, payload)
      .then((res) => {
        const r = res.data;
        setAviso({
          tipo: r.status === 'failed' ? 'warning' : 'success',
          texto: r.status === 'passed' ? `Aprobada con ${r.percent}%`
            : r.status === 'failed' ? `No aprobada (${r.percent}%)`
              : 'Actividad entregada',
        });
        return api.training.submissions(blockId)
          .then((s2) => setEnvios((prev) => ({ ...prev, [blockId]: s2.data?.items || [] })));
      })
      .catch((err) => setAviso({
        tipo: 'warning',
        texto: err?.response?.data?.detail || 'No se pudo enviar la actividad.',
      }));

  const idx = plano.findIndex((l) => l.id === leccion?.id);
  const anterior = idx > 0 ? plano[idx - 1] : null;
  const siguiente = idx >= 0 && idx < plano.length - 1 ? plano[idx + 1] : null;
  const completadas = new Set(
    (curso?.sections || []).flatMap((s) => s.lessons.filter((l) => l.completed).map((l) => l.id)));

  if (cargando && !curso) return <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
  if (!curso) return null;

  const Indice = (
    <Paper variant="outlined" sx={{ p: 1, height: '100%', overflow: 'auto' }}>
      {curso.sections.map((s) => (
        <Box key={s.id}>
          <ListItemButton onClick={() => setAbiertas((p) => ({ ...p, [s.id]: !p[s.id] }))}>
            <ListItemText
              primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 700 }}
              primary={s.title}
              secondary={s.locked ? s.locked_reason : undefined}
              secondaryTypographyProps={{ variant: 'caption', color: 'warning.main' }} />
            {abiertas[s.id] ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={abiertas[s.id]} unmountOnExit>
            <List dense disablePadding>
              {s.lessons.map((l) => (
                <ListItemButton key={l.id} selected={l.id === leccion?.id}
                  disabled={s.locked}
                  onClick={() => abrirLeccion(l.id)} sx={{ pl: 3 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {s.locked ? <LockIcon fontSize="small" color="disabled" />
                      : completadas.has(l.id)
                        ? <DoneIcon fontSize="small" color="success" />
                        : <PendingIcon fontSize="small" color="disabled" />}
                  </ListItemIcon>
                  <ListItemText primary={l.title}
                    primaryTypographyProps={{ variant: 'body2' }} />
                </ListItemButton>
              ))}
            </List>
          </Collapse>
        </Box>
      ))}
    </Paper>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <IconButton size="small" onClick={() => navigate('/training')} aria-label="Volver">
          <BackIcon />
        </IconButton>
        {isMobile && (
          <IconButton size="small" onClick={() => setIndiceVisible((v) => !v)} aria-label="Índice">
            <MenuIcon />
          </IconButton>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800} noWrap>{curso.title}</Typography>
        </Box>
        <Chip size="small" color={curso.progress.percent === 100 ? 'success' : 'default'}
          label={`${curso.progress.percent}%`} />
      </Stack>
      <LinearProgress variant="determinate" value={curso.progress.percent}
        color={curso.progress.percent === 100 ? 'success' : 'primary'}
        sx={{ height: 6, borderRadius: 3, mb: 2 }} />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
        {(indiceVisible || !isMobile) && (
          <Box sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0,
                     maxHeight: { md: '70vh' } }}>
            {Indice}
          </Box>
        )}

        <Paper variant="outlined" sx={{ flex: 1, p: { xs: 2, sm: 3 }, minWidth: 0, width: '100%' }}>
          {cargandoLeccion && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>}

          {!cargandoLeccion && leccion && (
            <>
              <Typography variant="h6" fontWeight={700}>{leccion.title}</Typography>
              {leccion.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {leccion.description}
                </Typography>
              )}
              <Divider sx={{ my: 2 }} />

              {aviso && (
                <Alert severity={aviso.tipo} sx={{ mb: 2 }} onClose={() => setAviso(null)}>
                  {aviso.texto}
                </Alert>
              )}

              <Stack spacing={2.5}>
                {(leccion.blocks || []).map((b) => (
                  <ContentBlock key={b.id} block={b}
                    checkedItems={leccion.checked_items || []}
                    onToggleChecklist={alternarItem}
                    onSubmitActivity={enviarActividad}
                    submissions={envios} />
                ))}
                {(leccion.blocks || []).length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Esta lección todavía no tiene contenido.
                  </Typography>
                )}
              </Stack>

              <Divider sx={{ my: 3 }} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}
                justifyContent="space-between">
                <Button size="small" startIcon={<NavigateBefore />} disabled={!anterior}
                  onClick={() => anterior && abrirLeccion(anterior.id)}>
                  Anterior
                </Button>
                <Button variant="contained"
                  color={completadas.has(leccion.id) ? 'success' : 'primary'}
                  startIcon={<DoneIcon />} onClick={completar}>
                  {completadas.has(leccion.id) ? 'Completada' : 'Marcar como completada'}
                </Button>
                <Button size="small" endIcon={<NavigateNext />} disabled={!siguiente}
                  onClick={() => siguiente && abrirLeccion(siguiente.id)}>
                  Siguiente
                </Button>
              </Stack>
            </>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};

export default TrainingCoursePage;
